import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../styles/SimulationResults.css';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, CssBaseline } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import IconButton from '@mui/material/IconButton';
import { FormControl, Select, MenuItem } from '@mui/material';

// Definicja ciemnego motywu z gradientami
const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        background: {
            default: 'linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%)',
            paper: '#2c2c2c',
        },
        text: {
            primary: '#ffffff',
            secondary: '#b0b0b0',
        },
    },
    typography: {
        fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
        h4: {
            fontWeight: 700,
            color: '#ffffff',
        },
        h5: {
            fontWeight: 600,
            color: '#ffffff',
        },
    },
    components: {
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    padding: '8px',
                    fontSize: '0.9rem',
                },
                head: {
                    backgroundColor: '#3a3a3a',
                    color: '#ffffff',
                    fontWeight: 600,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: '#2c2c2c',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&:disabled': {
                        color: 'rgba(255, 255, 255, 0.3)',
                    },
                },
            },
        },
        // Dodajemy style dla Select i MenuItem
        MuiFormControl: {
            styleOverrides: {
                root: {
                    minWidth: 200,
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '4px',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                },
                icon: {
                    color: '#ffffff',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: '#ffffff',
                    backgroundColor: '#2c2c2c',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#2c2c2c',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                },
            },
        },
    },
});

// Funkcje do obliczania kolorów gradientów (bez zmian)
const getGradientColor = (value) => {
    if (value === 0) return 'rgba(255, 255, 255, 0.1)';
    const scaledValue = Math.max(0, (value) / 0.8);
    const grayBase = 255;
    const r = Math.round(grayBase * (1 - scaledValue));
    const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
    const b = Math.round(grayBase * (1 - scaledValue));
    const alpha = 0.1 + scaledValue * 0.7;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getComparisonGradientColor = (value) => {
    if (value === 0) return 'rgba(255, 0, 0, 0.8)';
    const r = Math.round(255 * (1 - value));
    const g = Math.round(255 * value);
    const b = 0;
    return `rgba(${r}, ${g}, ${b}, 0.7)`;
};

const getTopGradientColor = (value) => {
    if (value === 0) return 'rgba(255, 255, 255, 0.1)';
    const scaledValue = value;
    const grayBase = 255;
    const r = Math.round(grayBase * (1 - scaledValue));
    const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
    const b = Math.round(grayBase * (1 - scaledValue));
    const alpha = 0.1 + scaledValue * 0.7;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getRelegationGradientColor = (value) => {
    if (value === 0) return 'rgba(255, 255, 255, 0.1)';
    const scaledValue = value;
    const grayBase = 255;
    const r = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
    const g = Math.round(grayBase * (1 - scaledValue));
    const b = Math.round(grayBase * (1 - scaledValue));
    const alpha = 0.1 + scaledValue * 0.7;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};



const SimulationResults = () => {
    const [data, setData] = useState({
        teamData: {},
        positionProbabilities: [],
        teamComparisons: [],
        createdAt: null // Zmieniamy nazwę pola
    });
    const [loading, setLoading] = useState(true);
    const [activeLeague, setActiveLeague] = useState('');
    const [competitions, setCompetitions] = useState({});
    const [selectedSimulationId, setSelectedSimulationId] = useState(null);
    const [simulationList, setSimulationList] = useState([]);
    const [currentSimulationIndex, setCurrentSimulationIndex] = useState(0);

// Pobierz dane o ligach z API i ustaw domyślną ligę
useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/competitions`)
        .then(response => {
            setCompetitions(response.data);

            // Ustaw domyślną ligę
            const levels = Object.keys(response.data).sort((a, b) => a - b);
            for (const level of levels) {
                const comps = response.data[level];
                const firstCompWithSimulation = comps.find(comp => comp.latest_simulation_id !== null);
                if (firstCompWithSimulation) {
                    const displayName = firstCompWithSimulation.group && firstCompWithSimulation.group !== "NULL"
                        ? `${firstCompWithSimulation.name} - ${firstCompWithSimulation.group.charAt(0).toUpperCase() + firstCompWithSimulation.group.slice(1)}`
                        : firstCompWithSimulation.name;
                    setActiveLeague(displayName);
                    setSelectedSimulationId(firstCompWithSimulation.latest_simulation_id);
                    setSimulationList(firstCompWithSimulation.simulation_ids || []);
                    setCurrentSimulationIndex(0);
                    break;
                }
            }
        })
        .catch(error => {
            console.error('Error fetching competitions:', error);
        });
}, []);

// Pobierz dane symulacji dla wybranego simulation_id
useEffect(() => {
    if (selectedSimulationId) {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API_URL}/api/simulation/${selectedSimulationId}`)
            .then(response => {
                console.log('Dane z backendu:', response.data);
                setData(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching simulation data:', error);
                setLoading(false);
            });
    }
}, [selectedSimulationId]);

// Funkcja do obsługi kliknięcia w ligę lub grupę
const handleLeagueClick = (leagueName, simulationId, group = null) => {
    if (simulationId === null) {
        setData({ teamData: {}, positionProbabilities: [], teamComparisons: [], createdAt: null });
        setSimulationList([]);
        setCurrentSimulationIndex(0);
        setLoading(false);
        return;
    }
    const displayName = group && group !== "NULL" ? `${leagueName} - ${group.charAt(0).toUpperCase() + group.slice(1)}` : leagueName;
    setActiveLeague(displayName);
    setSelectedSimulationId(simulationId);

    // Znajdź listę symulacji dla wybranej ligi
    const selectedComp = Object.values(competitions)
        .flat()
        .find(comp => {
            const compDisplayName = comp.group && comp.group !== "NULL"
                ? `${comp.name} - ${comp.group.charAt(0).toUpperCase() + comp.group.slice(1)}`
                : comp.name;
            return compDisplayName === displayName;
        });
    const simList = selectedComp?.simulation_ids || [];
    setSimulationList(simList);
    setCurrentSimulationIndex(0);
};

    if (loading) {
        return <Typography variant="h6">Ładowanie danych...</Typography>;
    }

    // Przygotuj listę drużyn i posortuj według expectedPoints
    const teams = Object.keys(data.teamData).map(teamId => ({
        id: parseInt(teamId),
        name: data.teamData[teamId].name,
        expectedPoints: data.teamData[teamId].expectedPoints ?? 0,
        expectedPlace: data.teamData[teamId].expectedPlace ?? 0
    })).sort((a, b) => b.expectedPoints - a.expectedPoints);

    const numTeams = teams.length;
	
	const handlePreviousSimulation = () => {
    if (currentSimulationIndex < simulationList.length - 1) {
        const newIndex = currentSimulationIndex + 1;
        setCurrentSimulationIndex(newIndex);
        setSelectedSimulationId(simulationList[newIndex].simulation_id);
    }
};

const handleNextSimulation = () => {
    if (currentSimulationIndex > 0) {
        const newIndex = currentSimulationIndex - 1;
        setCurrentSimulationIndex(newIndex);
        setSelectedSimulationId(simulationList[newIndex].simulation_id);
    }
};

const handleSimulationSelect = (event) => {
    const selectedIndex = event.target.value;
    setCurrentSimulationIndex(selectedIndex);
    setSelectedSimulationId(simulationList[selectedIndex].simulation_id);
};

    // Przygotuj dane do matrycy prawdopodobieństw pozycji
    const positionMatrix = teams.map(team => {
        const probs = Array(numTeams).fill(0);
        data.positionProbabilities
            .filter(prob => prob.teamId === team.id)
            .forEach(prob => {
                probs[prob.position - 1] = prob.probability;
            });
        return { teamId: team.id, teamName: team.name, probabilities: probs };
    });

    // Przygotuj dane do matrycy porównań drużyn
    const comparisonMatrix = teams.map(teamA => {
        const row = teams.map(teamB => {
            if (teamA.id === teamB.id) return 0.5;
            const comp = data.teamComparisons.find(
                c => (c.teamAId === teamA.id && c.teamBId === teamB.id) || (c.teamBId === teamA.id && c.teamAId === teamB.id)
            );
            if (!comp) return 0;
            return comp.teamAId === teamA.id ? comp.probabilityAOverB : 1 - comp.probabilityAOverB;
        });
        return { teamId: teamA.id, teamName: teamA.name, comparisons: row };
    });

    // Przygotuj dane do trzeciej tabeli (szanse na 1. miejsce, TOP3, spadek, expectedPlace, expectedPoints)
    const summaryMatrix = teams.map(team => {
        const probs = data.positionProbabilities.filter(prob => prob.teamId === team.id);
        
        const top1Chance = probs.find(prob => prob.position === 1)?.probability || 0;
        const top3Chance = probs
            .filter(prob => prob.position >= 1 && prob.position <= 3)
            .reduce((sum, prob) => sum + prob.probability, 0);
        const relegationChance = probs
            .filter(prob => prob.position >= numTeams - 2 && prob.position <= numTeams)
            .reduce((sum, prob) => sum + prob.probability, 0);

        return {
            teamId: team.id,
            teamName: team.name,
            top1Chance,
            top3Chance,
            relegationChance,
            expectedPlace: team.expectedPlace ?? 0,
            expectedPoints: team.expectedPoints ?? 0
        };
    });

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            {/* Nagłówek */}
            <header>
                <h1>SYMULIGA.PL</h1>
                <p>Obliczamy przyszłość futbolu</p>
            </header>

            {/* Menu Nawigacyjne */}
            <nav>
                <ul>
                    {Object.keys(competitions)
                        .sort((a, b) => a - b) // Sortuj poziomy rosnąco
                        .map(level => {
                            const comps = competitions[level];
                            // Sprawdź, czy liga ma grupy (group nie jest "NULL")
                            const hasGroups = comps.some(comp => comp.group && comp.group !== "NULL");

                            if (hasGroups) {
                                // Jeśli są grupy, generuj podmenu
                                return (
                                    <li key={level}>
                                        <a
                                            href="#"
                                            className={activeLeague.startsWith(comps[0].name) ? 'active' : ''}
                                        >
                                            {comps[0].name}
                                            <span className="material-icons">arrow_drop_down</span>
                                        </a>
                                        <ul className="dropdown">
                                            {comps
                                                .sort((a, b) => a.group.localeCompare(b.group)) // Sortuj grupy alfabetycznie
                                                .map(comp => (
                                                    <li key={comp.competition_id}>
                                                        <a
                                                            href="#"
                                                            onClick={() => handleLeagueClick(comp.name, comp.latest_simulation_id, comp.group)}
                                                            className={activeLeague === `${comp.name} - ${comp.group.charAt(0).toUpperCase() + comp.group.slice(1)}` ? 'active' : ''}
                                                        >
                                                            {comp.group.charAt(0).toUpperCase() + comp.group.slice(1)}
                                                        </a>
                                                    </li>
                                                ))}
                                        </ul>
                                    </li>
                                );
                            } else {
                                // Jeśli nie ma grup, generuj pojedynczy link
                                const comp = comps[0];
                                return (
                                    <li key={level}>
                                        <a
                                            href="#"
                                            className={activeLeague === comp.name ? 'active' : ''}
                                            onClick={() => handleLeagueClick(comp.name, comp.latest_simulation_id)}
                                        >
                                            {comp.name}
                                        </a>
                                    </li>
                                );
                            }
                        })}
                </ul>
            </nav>

            {/* Główna treść */}
            <main>
    <Box sx={{
        padding: { xs: 2, sm: 4 }, // Mniejszy padding na telefonach
        overflowX: 'auto',
    }}>
    <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' }, // Pionowo na telefonach, poziomo na większych ekranach
            justifyContent: { xs: 'center', sm: 'space-between' }, // Wyśrodkowanie na telefonach
            alignItems: 'center',
            mb: 2,
            gap: 2, // Odstęp między elementami
        }}
    >
        <Typography variant="h4" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            Wyniki Symulacji - {activeLeague}
        </Typography>
        {simulationList.length > 0 && (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap', // Zawijanie na mniejszych ekranach
                    justifyContent: 'center',
                    gap: 1,
                }}
            >
                <IconButton
                    onClick={handlePreviousSimulation}
                    disabled={currentSimulationIndex >= simulationList.length - 1}
                >
                    <ArrowBackIosIcon />
                </IconButton>
                <FormControl sx={{ mx: 1, minWidth: { xs: 200, sm: 250 } }}>
                    <Select
                        value={currentSimulationIndex}
                        onChange={handleSimulationSelect}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Wybierz symulację' }}
                    >
                        {simulationList.map((sim, index) => (
                            <MenuItem key={sim.simulation_id} value={index}>
                                {sim.created_at ? new Date(sim.created_at).toLocaleString() : 'Brak daty'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <IconButton
                    onClick={handleNextSimulation}
                    disabled={currentSimulationIndex === 0}
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </Box>
        )}
    </Box>

{numTeams === 0 ? (
    <Typography variant="h5" gutterBottom>
        Brak danych symulacji dla tej ligi.
    </Typography>
) : (
    <>
        {/* Prawdopodobieństwa Zajęcia Poszczególnych Miejsc */}
        <Typography variant="h5" gutterBottom>
            Prawdopodobieństwa Zajęcia Poszczególnych Miejsc
        </Typography>
        <TableContainer
            component={Paper}
            sx={{
                marginBottom: 1,
                width: '100%',
                overflowX: 'auto',
            }}
        >
            <Table sx={{ minWidth: { xs: 'auto', sm: 650 } }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: 100 }}>Drużyna</TableCell>
                        {Array.from({ length: numTeams }, (_, i) => (
                            <TableCell key={i} align="center" sx={{ minWidth: 50 }}>
                                P{i + 1}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {positionMatrix.map(row => (
                        <TableRow key={row.teamId}>
                            <TableCell>{row.teamName}</TableCell>
                            {row.probabilities.map((prob, index) => (
                                <TableCell
                                    key={index}
                                    align="center"
                                    sx={{
                                        backgroundColor: getGradientColor(prob),
                                        color: prob > 0.5 ? 'white' : 'black',
                                        transition: 'background-color 0.3s',
                                        '&:hover': {
                                            backgroundColor: getGradientColor(Math.min(prob + 0.1, 1)),
                                            transform: 'scale(1.05)',
                                        },
                                    }}
                                >
                                    {(prob * 100).toFixed(1)}%
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>

        {/* Prawdopodobieństwa Zakończenia na Wyższej Pozycji */}
        <Typography variant="h5" gutterBottom>
            Prawdopodobieństwa Zakończenia na Wyższej Pozycji
        </Typography>
        <TableContainer
            component={Paper}
            sx={{
                marginBottom: 1,
                width: '100%',
                overflowX: 'auto',
            }}
        >
            <Table sx={{ minWidth: { xs: 'auto', sm: 650 } }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: 100 }}>Drużyna</TableCell>
                        {teams.map(team => (
                            <TableCell key={team.id} align="center" sx={{ minWidth: 50 }}>
                                {team.name}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {comparisonMatrix.map(row => (
                        <TableRow key={row.teamId}>
                            <TableCell>{row.teamName}</TableCell>
                            {row.comparisons.map((prob, index) => (
                                <TableCell
                                    key={index}
                                    align="center"
                                    sx={{
                                        backgroundColor: prob === 0.5 ? 'rgba(255, 255, 255, 0.1)' : getComparisonGradientColor(prob),
                                        color: prob > 0.5 ? 'white' : 'black',
                                        transition: 'background-color 0.3s',
                                        '&:hover': {
                                            backgroundColor: prob === 0.5 ? 'rgba(255, 255, 255, 0.2)' : getComparisonGradientColor(Math.min(prob + 0.1, 1)),
                                            transform: 'scale(1.05)',
                                        },
                                    }}
                                >
                                    {prob === 0.5 ? '-' : (prob * 100).toFixed(1) + '%'}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>

        {/* Podsumowanie Symulacji */}
        <Typography variant="h5" gutterBottom>
            Podsumowanie Symulacji
        </Typography>
        <TableContainer
            component={Paper}
            sx={{
                width: '100%',
                overflowX: 'auto',
            }}
        >
            <Table sx={{ minWidth: { xs: 'auto', sm: 650 } }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: 100 }}>Drużyna</TableCell>
                        <TableCell align="center" sx={{ minWidth: 40 }}>Szansa na 1. Miejsce</TableCell>
                        <TableCell align="center" sx={{ minWidth: 40 }}>Szansa na TOP3</TableCell>
                        <TableCell align="center" sx={{ minWidth: 40 }}>Szansa na Spadek</TableCell>
                        <TableCell align="center" sx={{ minWidth: 40 }}>Śr. Miejsce</TableCell>
                        <TableCell align="center" sx={{ minWidth: 40 }}>Śr. Punkty</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {summaryMatrix.map(row => (
                        <TableRow key={row.teamId}>
                            <TableCell>{row.teamName}</TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    backgroundColor: getTopGradientColor(row.top1Chance),
                                    color: row.top1Chance > 0.5 ? 'white' : 'black',
                                    transition: 'background-color 0.3s',
                                    '&:hover': {
                                        backgroundColor: getTopGradientColor(Math.min(row.top1Chance + 0.1, 1)),
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                {(row.top1Chance * 100).toFixed(1)}%
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    backgroundColor: getTopGradientColor(row.top3Chance),
                                    color: row.top3Chance > 0.5 ? 'white' : 'black',
                                    transition: 'background-color 0.3s',
                                    '&:hover': {
                                        backgroundColor: getTopGradientColor(Math.min(row.top3Chance + 0.1, 1)),
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                {(row.top3Chance * 100).toFixed(1)}%
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    backgroundColor: getRelegationGradientColor(row.relegationChance),
                                    color: row.relegationChance > 0.5 ? 'white' : 'black',
                                    transition: 'background-color 0.3s',
                                    '&:hover': {
                                        backgroundColor: getRelegationGradientColor(Math.min(row.relegationChance + 0.1, 1)),
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                {(row.relegationChance * 100).toFixed(1)}%
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    transition: 'background-color 0.3s',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                {(row.expectedPlace || 0).toFixed(1)}
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    color: 'white',
                                    transition: 'background-color 0.3s',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                {(row.expectedPoints || 0).toFixed(1)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>

        {/* Sekcja z komunikatami o karach punktowych */}
        {data.penaltyMessages && data.penaltyMessages.length > 0 && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
                <Typography variant="h6" sx={{ mb: 1, color: '#ffffff', fontWeight: 600 }}>
                    Informacje o karach punktowych
                </Typography>
                {data.penaltyMessages.map((message, index) => (
                    <Typography
                        key={index}
                        variant="body2"
                        sx={{
                            color: '#ff6f61',
                            fontStyle: 'italic',
                            mb: 0.5,
                        }}
                    >
                        {message}
                    </Typography>
                ))}
            </Box>
        )}
    </>
)}
       
    </Box>
</main>

            {/* Stopka */}
            <footer>
                <p>© 2025 SymuLiga.pl. Wszystkie prawa zastrzeżone.</p>
            </footer>
        </ThemeProvider>
    );
};

export default SimulationResults;